<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import NetworkDiagnosticAPI from 'dashboard/api/networkDiagnostic';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const SAMPLE_COUNT = 8;

const { t } = useI18n();
const isRunning = ref(false);
const completedSamples = ref(0);
const samples = ref([]);
const serverInfo = ref(null);
const testedAt = ref(null);
const backofficeOrigin = window.location.origin;
const pageLoadMetrics = [
  { key: 'dns', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.DNS') },
  { key: 'tcp', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.TCP') },
  { key: 'tls', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.TLS') },
  { key: 'ttfb', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.TTFB') },
  { key: 'download', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.DOWNLOAD') },
  { key: 'total', label: t('NETWORK_DIAGNOSTICS.PAGE_LOAD.TOTAL') },
];

const round = value => Math.round(value * 10) / 10;
const formatMilliseconds = value =>
  value === null ? t('NETWORK_DIAGNOSTICS.NOT_AVAILABLE') : `${value} ms`;
const formatPercentage = value => `${value}%`;

const navigationMetrics = computed(() => {
  const navigation = performance.getEntriesByType('navigation')[0];
  if (!navigation) return null;

  return {
    dns: round(navigation.domainLookupEnd - navigation.domainLookupStart),
    tcp: round(navigation.connectEnd - navigation.connectStart),
    tls:
      navigation.secureConnectionStart > 0
        ? round(navigation.connectEnd - navigation.secureConnectionStart)
        : null,
    ttfb: round(navigation.responseStart - navigation.requestStart),
    download: round(navigation.responseEnd - navigation.responseStart),
    total: round(navigation.responseEnd - navigation.startTime),
  };
});

const browserConnection = computed(() => {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  return {
    online: navigator.onLine,
    effectiveType: connection?.effectiveType || null,
    estimatedRtt: connection?.rtt ?? null,
    estimatedDownlink: connection?.downlink ?? null,
  };
});

const successfulSamples = computed(() =>
  samples.value.filter(sample => sample.success).map(sample => sample.latency)
);

const summary = computed(() => {
  const latencies = successfulSamples.value;
  const failures = samples.value.length - latencies.length;

  if (!latencies.length) {
    return {
      minimum: null,
      average: null,
      p95: null,
      jitter: null,
      loss: samples.value.length ? 100 : 0,
      quality: 'unavailable',
    };
  }

  const sorted = [...latencies].sort((a, b) => a - b);
  const average =
    latencies.reduce((total, value) => total + value, 0) / latencies.length;
  const differences = latencies
    .slice(1)
    .map((value, index) => Math.abs(value - latencies[index]));
  const jitter = differences.length
    ? differences.reduce((total, value) => total + value, 0) /
      differences.length
    : 0;
  const p95 = sorted[Math.ceil(sorted.length * 0.95) - 1];
  const loss = (failures / samples.value.length) * 100;

  let quality = 'good';
  if (loss > 0 || p95 > 1000) quality = 'poor';
  else if (p95 > 400 || average > 250) quality = 'degraded';

  return {
    minimum: round(sorted[0]),
    average: round(average),
    p95: round(p95),
    jitter: round(jitter),
    loss: round(loss),
    quality,
  };
});

const qualityClasses = computed(
  () =>
    ({
      good: 'bg-n-teal-3 text-n-teal-11',
      degraded: 'bg-n-amber-3 text-n-amber-11',
      poor: 'bg-n-ruby-3 text-n-ruby-11',
      unavailable: 'bg-n-slate-3 text-n-slate-11',
    })[summary.value.quality]
);

const qualityLabel = computed(() => {
  if (summary.value.quality === 'good') {
    return t('NETWORK_DIAGNOSTICS.QUALITY.GOOD');
  }
  if (summary.value.quality === 'degraded') {
    return t('NETWORK_DIAGNOSTICS.QUALITY.DEGRADED');
  }
  if (summary.value.quality === 'poor') {
    return t('NETWORK_DIAGNOSTICS.QUALITY.POOR');
  }
  return t('NETWORK_DIAGNOSTICS.QUALITY.UNAVAILABLE');
});

const diagnosticReport = computed(() => ({
  tested_at: testedAt.value,
  backoffice_origin: backofficeOrigin,
  result: summary.value,
  samples: samples.value,
  initial_page_load: navigationMetrics.value,
  browser_connection: browserConnection.value,
  server_observation: serverInfo.value,
  user_agent: navigator.userAgent,
}));

const runDiagnostic = async () => {
  isRunning.value = true;
  completedSamples.value = 0;
  samples.value = [];
  serverInfo.value = null;
  testedAt.value = new Date().toISOString();

  for (let index = 0; index < SAMPLE_COUNT; index += 1) {
    const startedAt = performance.now();

    try {
      // Sequential requests measure latency without competing with each other.
      // eslint-disable-next-line no-await-in-loop
      const response = await NetworkDiagnosticAPI.run(`${Date.now()}-${index}`);
      samples.value.push({
        number: index + 1,
        success: true,
        latency: round(performance.now() - startedAt),
      });
      serverInfo.value = response.data;
    } catch {
      samples.value.push({
        number: index + 1,
        success: false,
        latency: null,
      });
    }

    completedSamples.value = index + 1;
  }

  isRunning.value = false;
};

const copyReport = async () => {
  await copyTextToClipboard(JSON.stringify(diagnosticReport.value, null, 2));
  useAlert(t('NETWORK_DIAGNOSTICS.COPY_SUCCESS'));
};

onMounted(runDiagnostic);
</script>

<template>
  <div class="flex-1 overflow-auto bg-n-background">
    <div class="grid max-w-screen-lg gap-8 px-5 py-12 mx-auto">
      <header
        class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="grid gap-2">
          <div class="flex items-center gap-3">
            <RouterLink
              :to="{ name: 'profile_settings_index' }"
              class="grid rounded-lg size-8 place-content-center text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="t('NETWORK_DIAGNOSTICS.BACK')"
            >
              <span class="i-lucide-arrow-left size-4" />
            </RouterLink>
            <h1 class="text-2xl font-medium text-n-slate-12">
              {{ t('NETWORK_DIAGNOSTICS.TITLE') }}
            </h1>
          </div>
          <p class="max-w-2xl text-sm text-n-slate-11">
            {{ t('NETWORK_DIAGNOSTICS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            v-if="samples.length"
            :label="t('NETWORK_DIAGNOSTICS.COPY_REPORT')"
            icon="i-lucide-copy"
            color="slate"
            outline
            :disabled="isRunning"
            @click="copyReport"
          />
          <Button
            :label="t('NETWORK_DIAGNOSTICS.RUN_AGAIN')"
            icon="i-lucide-refresh-cw"
            :is-loading="isRunning"
            :disabled="isRunning"
            @click="runDiagnostic"
          />
        </div>
      </header>

      <div
        v-if="isRunning"
        class="flex items-center gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 text-n-slate-11"
      >
        <Spinner :size="18" />
        <span>
          {{
            t('NETWORK_DIAGNOSTICS.RUNNING', {
              current: completedSamples,
              total: SAMPLE_COUNT,
            })
          }}
        </span>
      </div>

      <template v-if="samples.length">
        <section
          class="grid gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
        >
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('NETWORK_DIAGNOSTICS.RESULT.TITLE') }}
            </h2>
            <span
              class="px-2.5 py-1 text-xs font-medium rounded-full"
              :class="qualityClasses"
            >
              {{ qualityLabel }}
            </span>
          </div>
          <dl class="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <div class="grid gap-1 p-3 rounded-lg bg-n-alpha-1">
              <dt class="text-xs text-n-slate-10">
                {{ t('NETWORK_DIAGNOSTICS.RESULT.AVERAGE') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatMilliseconds(summary.average) }}
              </dd>
            </div>
            <div class="grid gap-1 p-3 rounded-lg bg-n-alpha-1">
              <dt class="text-xs text-n-slate-10">
                {{ t('NETWORK_DIAGNOSTICS.RESULT.P95') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatMilliseconds(summary.p95) }}
              </dd>
            </div>
            <div class="grid gap-1 p-3 rounded-lg bg-n-alpha-1">
              <dt class="text-xs text-n-slate-10">
                {{ t('NETWORK_DIAGNOSTICS.RESULT.JITTER') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatMilliseconds(summary.jitter) }}
              </dd>
            </div>
            <div class="grid gap-1 p-3 rounded-lg bg-n-alpha-1">
              <dt class="text-xs text-n-slate-10">
                {{ t('NETWORK_DIAGNOSTICS.RESULT.LOSS') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatPercentage(summary.loss) }}
              </dd>
            </div>
            <div class="grid gap-1 p-3 rounded-lg bg-n-alpha-1">
              <dt class="text-xs text-n-slate-10">
                {{ t('NETWORK_DIAGNOSTICS.RESULT.MINIMUM') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatMilliseconds(summary.minimum) }}
              </dd>
            </div>
          </dl>
        </section>

        <div class="grid gap-6 lg:grid-cols-2">
          <section
            class="grid content-start gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
          >
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('NETWORK_DIAGNOSTICS.PATH.TITLE') }}
            </h2>
            <dl class="grid gap-3 text-sm">
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-11">
                  {{ t('NETWORK_DIAGNOSTICS.PATH.ORIGIN') }}
                </dt>
                <dd class="font-mono text-right break-all text-n-slate-12">
                  {{ backofficeOrigin }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-11">
                  {{ t('NETWORK_DIAGNOSTICS.PATH.OBSERVED_IP') }}
                </dt>
                <dd class="font-mono text-n-slate-12">
                  {{
                    serverInfo?.observed_ip ||
                    t('NETWORK_DIAGNOSTICS.NOT_AVAILABLE')
                  }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-11">
                  {{ t('NETWORK_DIAGNOSTICS.PATH.PROTOCOL') }}
                </dt>
                <dd class="font-mono text-n-slate-12">
                  {{
                    serverInfo?.http_protocol ||
                    t('NETWORK_DIAGNOSTICS.NOT_AVAILABLE')
                  }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-11">
                  {{ t('NETWORK_DIAGNOSTICS.PATH.NETWORK_TYPE') }}
                </dt>
                <dd class="font-mono text-n-slate-12">
                  {{
                    browserConnection.effectiveType ||
                    t('NETWORK_DIAGNOSTICS.NOT_AVAILABLE')
                  }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-11">
                  {{ t('NETWORK_DIAGNOSTICS.PATH.REQUEST_ID') }}
                </dt>
                <dd
                  class="max-w-xs font-mono text-xs text-right break-all text-n-slate-12"
                >
                  {{
                    serverInfo?.request_id ||
                    t('NETWORK_DIAGNOSTICS.NOT_AVAILABLE')
                  }}
                </dd>
              </div>
            </dl>
          </section>

          <section
            class="grid content-start gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
          >
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('NETWORK_DIAGNOSTICS.PAGE_LOAD.TITLE') }}
            </h2>
            <dl class="grid grid-cols-2 gap-3 text-sm">
              <template v-if="navigationMetrics">
                <div
                  v-for="metric in pageLoadMetrics"
                  :key="metric.key"
                  class="flex justify-between gap-3 p-2 rounded-lg bg-n-alpha-1"
                >
                  <dt class="text-n-slate-11">
                    {{ metric.label }}
                  </dt>
                  <dd class="font-mono text-n-slate-12">
                    {{ formatMilliseconds(navigationMetrics[metric.key]) }}
                  </dd>
                </div>
              </template>
            </dl>
            <p class="text-xs text-n-slate-10">
              {{ t('NETWORK_DIAGNOSTICS.PAGE_LOAD.NOTE') }}
            </p>
          </section>
        </div>

        <section
          class="grid gap-3 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
        >
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('NETWORK_DIAGNOSTICS.SAMPLES.TITLE') }}
          </h2>
          <div class="grid grid-cols-4 gap-2 sm:grid-cols-8">
            <div
              v-for="sample in samples"
              :key="sample.number"
              class="grid gap-1 p-2 text-center rounded-lg bg-n-alpha-1"
            >
              <span class="text-xs text-n-slate-10">
                {{
                  t('NETWORK_DIAGNOSTICS.SAMPLES.NUMBER', {
                    number: sample.number,
                  })
                }}
              </span>
              <span
                class="font-mono text-sm"
                :class="sample.success ? 'text-n-slate-12' : 'text-n-ruby-11'"
              >
                {{
                  sample.success
                    ? formatMilliseconds(sample.latency)
                    : t('NETWORK_DIAGNOSTICS.SAMPLES.FAILED')
                }}
              </span>
            </div>
          </div>
        </section>

        <aside
          class="flex gap-3 p-4 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
        >
          <span class="flex-shrink-0 i-lucide-info size-5" />
          <p>{{ t('NETWORK_DIAGNOSTICS.LIMITATION') }}</p>
        </aside>
      </template>
    </div>
  </div>
</template>
